import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Facebook, Instagram, Twitter, Youtube, Waves } from 'lucide-react';
import { useComponentTranslations } from '../hooks/useTranslations';

const Footer = () => {
  const { t, loading } = useComponentTranslations('footer');

  if (loading) {
    return (
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="space-y-4">
                <div className="h-6 bg-gray-700 rounded animate-pulse"></div>
                <div className="h-4 bg-gray-700 rounded animate-pulse"></div>
                <div className="h-4 bg-gray-700 rounded animate-pulse"></div>
              </div>
            ))}
          </div>
        </div>
      </footer>
    );
  }

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer className="bg-gray-900 text-white">
      {/* CTA Section */}
      <div className="bg-gradient-to-r from-evergreen to-dream-teal py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6"
          >
            {t('cta', 'Ready to Optimize Your Operations?')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto"
          >
            {t('cta_desc', 'Discover how our management consulting services can streamline your business operations and drive sustainable growth')}
          </motion.p>
          <motion.button
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            onClick={() => scrollToSection('contact')}
            className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-colors duration-300 transform hover:scale-105 shadow-lg"
          >
            {t('get_started', 'Get Started Now')}
          </motion.button>
        </div>
      </div>

      {/* Main Footer */}
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            
            {/* Company Info */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-2"
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-dream-teal to-evergreen rounded-xl flex items-center justify-center">
                  <span className="text-white font-bold text-xl">D</span>
                </div>
                <div>
                  <h3 className="text-2xl font-bold">{t('company_name', 'Dreamline Operations LLC')}</h3>
                  <p className="text-gray-400">{t('tagline', 'Streamline. Strengthen. Succeed.')}</p>
                </div>
              </div>
              <p className="text-gray-400 mb-6 leading-relaxed max-w-md">
                {t('description', 'Management consulting and operational excellence for businesses worldwide. We drive sustainable growth through innovative operations management practices.')}
              </p>
              
              {/* Social Links */}
              <div className="flex space-x-4">
                {[
                  { icon: Facebook, href: '#' },
                  { icon: Instagram, href: '#' },
                  { icon: Twitter, href: '#' },
                  { icon: Youtube, href: '#' }
                ].map((social, index) => (
                  <a
                    key={index}
                    href={social.href}
                    className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-blue-600 transition-colors duration-300"
                  >
                    <social.icon className="w-5 h-5" />
                  </a>
                ))}
              </div>
            </motion.div>

            {/* Quick Links */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
            >
              <h4 className="text-lg font-semibold mb-6">{t('quick_links', 'Quick Links')}</h4>
              <ul className="space-y-3">
                {[
                  { label: t('link_home', 'Home'), id: 'hero' },
                  { label: t('link_services', 'Services'), id: 'services' },
                  { label: t('link_about', 'About'), id: 'about' },
                  { label: t('link_insights', 'Insights'), id: 'insights' },
                  { label: t('link_contact', 'Contact'), id: 'contact' }
                ].map((link, index) => (
                  <li key={index}>
                    <button
                      onClick={() => scrollToSection(link.id)}
                      className="text-gray-400 hover:text-white transition-colors duration-300"
                    >
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h4 className="text-lg font-semibold mb-6">{t('contact_info', 'Contact Information')}</h4>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-blue-400" />
                  <a href="mailto:contact@dreamline.company" className="text-gray-400 hover:text-blue-400 transition-colors duration-300">
                    contact@dreamline.company
                  </a>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-teal-400" />
                  <div className="text-gray-400">
                    <div>Toll Free: <a href="tel:+18668844248" className="hover:text-teal-400 transition-colors duration-300">866-884-4248</a></div>
                    <div>Local: <a href="tel:+16573004248" className="hover:text-teal-400 transition-colors duration-300">657-300-4248</a></div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <MapPin className="w-5 h-5 text-purple-400" />
                  <span className="text-gray-400">{t('operations_center', 'Operations Center')}</span>
                </div>
              </div>
              
              {/* Business Hours */}
              <div className="mt-6">
                <h5 className="text-sm font-semibold text-gray-300 mb-3">{t('business_hours', 'Business Hours')}</h5>
                <div className="text-sm text-gray-400 space-y-1">
                  <div>{t('monday_friday', 'Monday - Friday')}: 9:00 AM - 4:00 PM MDT</div>
                  <div>{t('weekend', 'Weekend')}: {t('by_appointment', 'By Appointment')}</div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Bottom Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="border-t border-gray-800 pt-8"
          >
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="text-gray-400 text-sm mb-4 md:mb-0">
                © {new Date().getFullYear()} {t('company_name', 'Dreamline Operations LLC')}. {t('rights', 'All rights reserved.')}
              </div>
              <div className="flex space-x-6 text-sm text-gray-400">
                <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors duration-300">
                  {t('privacy', 'Privacy Policy')}
                </a>
                <a href="/terms-of-service.html" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors duration-300">
                  {t('terms', 'Terms of Service')}
                </a>
                <a href="/privacy-policy.html#cookies" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors duration-300">
                  {t('cookies', 'Cookie Policy')}
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Decorative Wave */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-evergreen via-dream-teal to-coral"></div>
    </footer>
  );
};

export default Footer;